import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const ParishRegistrationViewerPage = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noteDialog, setNoteDialog] = useState({ open: false, content: '' });

  useEffect(() => {
    fetchRegistration();
  }, [registrationId]);

  const fetchRegistration = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/parish-registration/${registrationId}`);
      setRegistration(response.data);
    } catch (error) {
      console.error('Error fetching registration:', error);
      setError('Failed to load registration');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    try {
      await axios.post(`/api/parish-registration/${registrationId}/notes`, {
        content: noteDialog.content
      });
      setNoteDialog({ open: false, content: '' });
      fetchRegistration();
    } catch (error) {
      console.error('Error adding note:', error);
      setError('Failed to add note');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderPersonCard = (person, title) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom color="primary">
          {title}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Name</Typography>
            <Typography variant="body1">
              {person.firstName} {person.lastName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Gender</Typography>
            <Typography variant="body1">{person.gender || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
            <Typography variant="body1">{formatDate(person.dateOfBirth)}</Typography>
          </Grid>
          {person.occupation && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Occupation</Typography>
              <Typography variant="body1">{person.occupation}</Typography>
            </Grid>
          )}
          {person.employer && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Employer</Typography>
              <Typography variant="body1">{person.employer}</Typography>
            </Grid>
          )}
          {person.relationship && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Relationship</Typography>
              <Typography variant="body1">{person.relationship}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Primary Language</Typography>
            <Typography variant="body1">{person.primaryLanguage || 'English'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">Current Religion</Typography>
            <Typography variant="body1">{person.currentReligion || 'Catholic'}</Typography>
          </Grid>
        </Grid>

        {person.sacraments && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Sacraments</Typography>
            <Table size="small">
              <TableBody>
                {Object.entries(person.sacraments).map(([sacrament, data]) => (
                  <TableRow key={sacrament}>
                    <TableCell sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                      {sacrament}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={data.received || 'N/A'}
                        size="small"
                        color={data.received === 'Yes' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{data.where || ''}</TableCell>
                    <TableCell>{data.when || ''}</TableCell>
                    {data.interested && (
                      <TableCell>
                        <Chip
                          label={`Interested: ${data.interested}`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!registration) {
    return (
      <Container>
        <Alert severity="warning">Registration not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/parish-registration')}
          >
            Back to List
          </Button>
          <Typography variant="h4">
            {registration.family.firstNames} {registration.family.lastName} Family
          </Typography>
          <Chip
            label={registration.status}
            color={getStatusColor(registration.status)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<NoteIcon />}
            onClick={() => setNoteDialog({ open: true, content: '' })}
          >
            Add Note
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/parish-registration')}
          >
            Back to List to Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Family Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Family Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Family Name</Typography>
                <Typography variant="body1">
                  {registration.family.firstNames} {registration.family.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{registration.family.contact.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Address</Typography>
                <Typography variant="body1">
                  {registration.family.address.street}<br />
                  {registration.family.address.city}, {registration.family.address.state} {registration.family.address.zipCode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Phone Numbers</Typography>
                <Typography variant="body1">
                  {registration.family.contact.homePhone && (
                    <>Home: {registration.family.contact.homePhone}<br /></>
                  )}
                  {registration.family.contact.cellPhone && (
                    <>Cell: {registration.family.contact.cellPhone}</>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Primary Contact */}
        <Grid item xs={12}>
          {renderPersonCard(registration.primaryContact, 'Primary Contact')}
        </Grid>

        {/* Marriage Information */}
        {registration.primaryContact.marriage?.status && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Marriage Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Typography variant="body1">{registration.primaryContact.marriage.status}</Typography>
                </Grid>
                {registration.primaryContact.marriage.location && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Location</Typography>
                    <Typography variant="body1">{registration.primaryContact.marriage.location}</Typography>
                  </Grid>
                )}
                {registration.primaryContact.marriage.date && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Date</Typography>
                    <Typography variant="body1">{formatDate(registration.primaryContact.marriage.date)}</Typography>
                  </Grid>
                )}
                {registration.primaryContact.marriage.catholicChurch && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Catholic Church</Typography>
                    <Typography variant="body1">{registration.primaryContact.marriage.catholicChurch}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Spouse */}
        {registration.spouse && registration.spouse.firstName && (
          <Grid item xs={12}>
            {renderPersonCard(registration.spouse, 'Spouse Information')}
          </Grid>
        )}

        {/* Children */}
        {registration.children && registration.children.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              Children
            </Typography>
            {registration.children.map((child, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {renderPersonCard(child, `Child ${index + 1}`)}
              </Box>
            ))}
          </Grid>
        )}

        {/* Additional Adults */}
        {registration.additionalAdults && registration.additionalAdults.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              Additional Adults
            </Typography>
            {registration.additionalAdults.map((adult, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {renderPersonCard(adult, `Additional Adult ${index + 1}`)}
              </Box>
            ))}
          </Grid>
        )}

        {/* Notes and Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Notes & Activity
            </Typography>

            {registration.notes && registration.notes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Notes</Typography>
                {registration.notes.map((note, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">{note.content}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {note.author.name} - {formatDateTime(note.date)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {registration.activity && registration.activity.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>Activity Log</Typography>
                {registration.activity.map((activity, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>{activity.action}</strong>
                      {activity.user && ` by ${activity.user.name}`}
                      {activity.details && ` - ${activity.details}`}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDateTime(activity.date)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Note Dialog */}
      <Dialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, content: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={noteDialog.content}
            onChange={(e) => setNoteDialog({ ...noteDialog, content: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, content: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleAddNote}
            variant="contained"
            disabled={!noteDialog.content.trim()}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ParishRegistrationViewerPage;